import { API, graphqlOperation, Storage } from '../utils/amplifyCompat'
import {
  createNotificationFromComment,
  createNotificationFromLovedJournal,
  createNotificationFromLovedComment,
} from './notification'
import { adjustUserJournalCount, buildS3Url, ownPublicPath } from './user'
import { refusalOf, MEMBERS_ONLY } from '../utils/refusal'
import * as custom_queries from '../customgraphql/queries'
import * as custom_mutations from '../customgraphql/mutations'
import { alreadyExists } from '../utils/alreadyExists'
import { graphqlGetUser } from '../utils/getUserTolerant'

// One API call of a journal save. Amplify throws when a response has any errors; a response
// whose only errors are on nested fields (a related row the caller can't read) still did its
// write, so it counts as done and the next step runs. Any other error is thrown.
async function gql(operation) {
  try {
    return await API.graphql(operation)
  } catch (err) {
    const nestedOnly =
      Array.isArray(err?.errors) && err.errors.length > 0 && err.errors.every((e) => Array.isArray(e?.path) && e.path.length > 1)
    if (nestedOnly && err.data && Object.values(err.data).some(Boolean)) return err
    throw err
  }
}

// A love's ID: one per user and journal (or comment), so loving twice can't count twice.
// Older loves keep their random IDs; they are found through the GSIs as before.
const loveId = (userId, targetId) => `${userId}#${targetId}`

// Create a Journal
// -----------------------------------------------------------------------------
export function saveJournal(journal) {
  return async (dispatch) => {
    dispatch({ type: 'REQUEST_CREATE_JOURNAL' })
    try {
      const data = await gql(
        graphqlOperation(custom_mutations.createJournal, { input: journal })
      )
      dispatch({
        type: 'REQUEST_CREATE_JOURNAL_SUCCESS',
        journalCreated: data.data.createJournal,
      })
      dispatch(adjustUserJournalCount(1))
      dispatch(
        checkIfUserHasJournalAlready({
          userUid: journal.journalAuthorId,
          journalWordCountPerMonth: 0,
          request: 'WRITE',
        })
      )
      return data.data.createJournal
    } catch (err) {
      console.log('error creating journal: ', err)
      dispatch({ type: 'REQUEST_CREATE_JOURNAL_FAILED' })
      // The page must know: it takes the new journal's ID from this result, never from
      // currentJournal, which may still be another journal (review D1c).
      throw err
    }
  }
}

// Update Journal
// -----------------------------------------------------------------------------
export function updateJournal(journal) {
  return async (dispatch) => {
    dispatch({ type: 'REQUEST_UPDATE_JOURNAL' })
    let result
    try {
      if (journal.draft === false) {
        const updateJournalRow = () =>
          gql(
            graphqlOperation(custom_mutations.updateJournal, {
              input: {
                id: journal.id,
                content: journal.content,
                privacy: journal.privacy,
                journalAuthorId: journal.journalAuthorId,
                draft: journal.draft,
                journalWordCount: journal.journalWordCount,
                journalBookId: journal.journalBookId
                  ? journal.journalBookId
                  : null,
                createdAt: journal.createdAt,
              },
            })
          )
        // PublicJournal logics
        if (journal.privacy === 'PUBLIC') {
          // The PublicJournal row first: the server can refuse it (members only, security sprint
          // PR 9), and a refusal must leave the Journal and the author's LATEST journal unchanged.
          const checkPublicJournal = await gql(
            graphqlOperation(custom_queries.getPublicJournal, {
              id: journal.id,
            })
          )
          const existing = checkPublicJournal.data.getPublicJournal
          if (existing) {
            // Is user editing the exisitng PUBLIC journal -> updatePublicJournal
            await gql(
              graphqlOperation(custom_mutations.updatePublicJournal, {
                input: {
                  id: journal.id,
                  content: journal.content,
                  privacy: 'PUBLIC',
                  journalAuthorId: journal.journalAuthorId,
                  journalWordCount: journal.journalWordCount,
                  publicJournalBookId: journal.journalBookId
                    ? journal.journalBookId
                    : null,
                  createdAt: journal.createdAt,
                },
              })
            )
          } else {
            // Is user creating a new PUBLIC journal -> createPublicJournal
            // Is user changing PRIVATE journal to PUBLIC journal -> createPublicJournal
            let publicJournalData = {
              id: journal.id,
              content: journal.content,
              privacy: 'PUBLIC',
              journalAuthorId: journal.journalAuthorId,
              markedAs: 'LATEST',
              // Numbers, never null: the counters Lambda only adds to a missing or numeric count.
              lovedCount: journal.lovedCount || 0,
              commentCount: journal.commentCount || 0,
              journalWordCount: journal.journalWordCount,
              createdAt: journal.createdAt,
            }
            if (journal.journalBookId)
              publicJournalData.publicJournalBookId = journal.journalBookId

            await gql(
              graphqlOperation(custom_mutations.createPublicJournal, {
                input: publicJournalData,
              })
            )
          }
          result = await updateJournalRow()
          // This journal is now the author's LATEST: clear the marker on their other public
          // journals. Also on a retried Publish whose first try stopped after the create.
          if (!existing || existing.markedAs === 'LATEST') {
            const getLatestJournals = await gql(
              graphqlOperation(custom_queries.markedAsAuthorAsKey, {
                markedAs: 'LATEST',
                journalAuthorId: { eq: journal.journalAuthorId },
                limit: 4, // this journal and up to 3 others, as before
              })
            )
            for (const publicJournalLatest of getLatestJournals.data
              .markedAsAuthorAsKey.items) {
              if (publicJournalLatest.id === journal.id) continue
              await gql(
                graphqlOperation(custom_mutations.updatePublicJournal, {
                  input: {
                    id: publicJournalLatest.id,
                    markedAs: null,
                  },
                })
              )
            }
          }
        } else {
          result = await updateJournalRow()
          // Is user changing PUBLIC to PRIVATE journal -> deletePublicJournal
          const checkPublicJournal = await gql(
            graphqlOperation(custom_queries.getPublicJournal, {
              id: journal.id,
            })
          )
          if (checkPublicJournal.data.getPublicJournal) {
            const publicLatestJournal = await gql(
              graphqlOperation(custom_mutations.deletePublicJournal, {
                input: { id: journal.id },
              })
            )
            // if deleted journal is LATEST public journal, find the next latest public journal and assign LATEST
            if (
              publicLatestJournal.data.deletePublicJournal.markedAs === 'LATEST'
            ) {
              const latestJournalData = await gql(
                graphqlOperation(custom_queries.journalAuthorLatestAsKey, {
                  journalAuthorId:
                    publicLatestJournal.data.deletePublicJournal
                      .journalAuthorId,
                  sortDirection: 'DESC',
                })
              )
              if (
                latestJournalData.data.journalAuthorLatestAsKey.items.length > 0
              ) {
                await gql(
                  graphqlOperation(custom_mutations.updatePublicJournal, {
                    input: {
                      id: latestJournalData.data.journalAuthorLatestAsKey
                        .items[0].id,
                      markedAs: 'LATEST',
                    },
                  })
                )
              }
            }
          }
        }
        // After every step succeeded, so a retried Publish can't count the words twice.
        dispatch(
          checkIfUserHasJournalAlready({
            userUid: journal.journalAuthorId,
            journalWordCountBeforeEditing:
              journal.journalWordCountBeforeEditing,
            journalWordCountPerMonth: journal.journalWordCount,
            request: 'UPDATE',
          })
        )
        dispatch({
          type: 'REQUEST_UPDATE_JOURNAL_SUCCESS',
          journalUpdated:
            journal.privacy === 'PUBLIC'
              ? result.data.updatePublicJournal
              : result.data.updateJournal,
        })
      } else {
        let resetData = {
          id: journal.id,
          content: journal.content,
          privacy: journal.privacy,
          journalAuthorId: journal.journalAuthorId,
          journalWordCount: journal.journalWordCount,
          draft: journal.draft,
          journalBookId: journal.journalBookId ? journal.journalBookId : null,
          createdAt: journal.createdAt,
        }
        result = await gql(
          graphqlOperation(custom_mutations.updateJournal, { input: resetData })
        )
        dispatch({
          type: 'REQUEST_UPDATE_JOURNAL_SUCCESS',
          journalUpdated: result.data.updateJournal,
        })
      }
      return result
    } catch (err) {
      console.log('error updating journal: ', err)
      dispatch({ type: 'REQUEST_UPDATE_JOURNAL_FAILED' })
      throw err
    }
  }
}

// Get Journal
// -----------------------------------------------------------------------------
export function getJournal(journalUid) {
  return async (dispatch) => {
    dispatch({ type: 'JOURNAL_LOADING_STATUS', loading: true })
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.getJournal, { id: journalUid })
      )
      dispatch({
        type: 'JOURNAL_AVAILABLE',
        currentJournal: data.data.getJournal,
      })
      return data.data.getJournal
    } catch (err) {
      console.log('error getting journal: ', err)
    }
  }
}

// Delete Journal
// -----------------------------------------------------------------------------
export function deleteJournal(journalUid) {
  return async (dispatch) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.deleteJournal, {
          input: { id: journalUid },
        })
      )
      // Remove public journal if it's a public journal
      if (data.data.deleteJournal.privacy === 'PUBLIC') {
        const publicLatestJournal = await API.graphql(
          graphqlOperation(custom_mutations.deletePublicJournal, {
            input: { id: journalUid },
          })
        )
        // if deleted journal is LATEST public journal, find the next latest public journal and assign LATEST
        if (
          publicLatestJournal.data.deletePublicJournal &&
          publicLatestJournal.data.deletePublicJournal.markedAs === 'LATEST'
        ) {
          const latestJournalData = await API.graphql(
            graphqlOperation(custom_queries.journalAuthorLatestAsKey, {
              journalAuthorId:
                publicLatestJournal.data.deletePublicJournal.journalAuthorId,
              sortDirection: 'DESC',
            })
          )
          if (
            latestJournalData.data.journalAuthorLatestAsKey.items.length > 0
          ) {
            await API.graphql(
              graphqlOperation(custom_mutations.updatePublicJournal, {
                input: {
                  id: latestJournalData.data.journalAuthorLatestAsKey.items[0]
                    .id,
                  markedAs: 'LATEST',
                },
              })
            )
          }
        }
      }
      dispatch(adjustUserJournalCount(-1))
      // Update book bookJournalCount count number with -1 if journalBookId exist
      if (
        data.data.deleteJournal.journalBookId &&
        data.data.deleteJournal.journalBookId !== null
      ) {
        dispatch(
          adjustBookJournalsCount(data.data.deleteJournal.journalBookId, -1)
        )
      }

      dispatch(
        checkIfUserHasJournalAlready({
          userUid: data.data.deleteJournal.author.id,
          journalWordCountPerMonth: data.data.deleteJournal.journalWordCount,
          request: 'DELETE',
        })
      )

      return data.data.deleteJournal
    } catch (err) {
      console.log('error deleteJournal: ', err)
    }
  }
}

// Set Journal
// -----------------------------------------------------------------------------
export function setJournal(journal) {
  return (dispatch) => {
    dispatch({ type: 'JOURNAL_TEMP_SET', journal })
  }
}

// Set current Public journal empty
// -----------------------------------------------------------------------------
export function setEmptyCurrentJournal() {
  return (dispatch) => {
    dispatch({ type: 'JOURNAL_AVAILABLE', currentJournal: null })
  }
}

// Create a Comment
// -----------------------------------------------------------------------------
export function createComment(comment, journalAuthorId) {
  return async (dispatch, getState) => {
    try {
      // gql: a comment written with only a related row in the response unreadable is still
      // a comment, shown and notified like any other (so it isn't posted twice).
      const data = await gql(
        graphqlOperation(custom_mutations.createComment, { input: comment })
      )
      // Shown locally; gnjAmplifyCounters updates the stored count (security sprint PR 5).
      let newCommentCount = (getState().journal.currentJournal.commentCount || 0) + 1
      createNotificationFromComment(
        { ...comment, id: data.data.createComment.id },
        journalAuthorId,
        getState().user.userData
      )

      getState().journal.currentJournal.comments.items = [
        ...getState().journal.currentJournal.comments.items,
        data.data.createComment,
      ]
      getState().journal.currentJournal.commentCount = newCommentCount
      dispatch({
        type: 'JOURNAL_AVAILABLE',
        currentJournal: getState().journal.currentJournal,
      })
    } catch (err) {
      // Not written (refused, or a network error): the page says so (containers/Comments.js).
      console.log('error commenting: ', err)
      throw err
    }
  }
}

// Update Comment
// -----------------------------------------------------------------------------
export function updateComment(comment, newComment) {
  return async (dispatch, getState) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.updateComment, {
          input: { id: comment.id, content: newComment },
        })
      )
      let findUpdatedComment =
        getState().journal.currentJournal.comments.items.findIndex(
          (x) => x.id === data.data.updateComment.id
        )
      getState().journal.currentJournal.comments.items[findUpdatedComment] =
        data.data.updateComment
      getState().journal.currentJournal.comments.items[findUpdatedComment][
        'hasLoved'
      ] = comment.hasLoved
      dispatch({
        type: 'JOURNAL_AVAILABLE',
        currentJournal: getState().journal.currentJournal,
      })
    } catch (err) {
      console.log('error updating comment: ', err)
    }
  }
}

// Delete Comment
// -----------------------------------------------------------------------------
export function deleteComment(commentId) {
  return async (dispatch, getState) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.deleteComment, { input: commentId })
      )
      let newCommentCount =
        getState().journal.currentJournal.commentCount > 0
          ? getState().journal.currentJournal.commentCount - 1
          : 0

      getState().journal.currentJournal.comments.items =
        getState().journal.currentJournal.comments.items.filter(function (obj) {
          return obj.id !== data.data.deleteComment.id
        })
      getState().journal.currentJournal.commentCount = newCommentCount
      dispatch({
        type: 'JOURNAL_AVAILABLE',
        currentJournal: getState().journal.currentJournal,
      })
    } catch (err) {
      console.log('error deleteing comment: ', err)
    }
  }
}

// Get more comment
// -----------------------------------------------------------------------------
export function getMoreComments(journalUid, nextToken) {
  return async (dispatch, getState) => {
    try {
      const data = await API.graphql({
        query: custom_queries.getPublicJournal,
        variables: {
          id: journalUid,
          nextTokenComment: nextToken,
        },
        authMode: 'AWS_IAM',
      })
      if (nextToken) {
        return data.data.getPublicJournal.comments
      }
      dispatch({
        type: 'JOURNAL_AVAILABLE',
        currentJournal: data.data.getPublicJournal,
      })
    } catch (err) {
      console.log('error getting comment: ', err)
    }
  }
}

// Get Public Journal List
// -----------------------------------------------------------------------------
export function getPublicJournals(limit, nextToken) {
  return async () => {
    try {
      const data = await API.graphql({
        query: custom_queries.publicJournalMarkedAsKey,
        variables: {
          markedAs: 'LATEST',
          limit: limit,
          sortDirection: 'DESC',
          nextToken,
        },
        authMode: 'AWS_IAM',
      })
      return data.data.publicJournalMarkedAsKey
    } catch (err) {
      console.log('error getting journal: ', err)
    }
  }
}

export function setCommunityLimit(limit) {
  return (dispatch) => {
    dispatch({ type: 'SET_COMMUNITY_LIMIT', limit })
  }
}

// Filter Public Journal List
// -----------------------------------------------------------------------------
export function searchPublicJournals(value, nextToken) {
  return async () => {
    try {
      // TODO: search needs to be improved because query and filter only covers the data that are queried.
      const data = await API.graphql(
        graphqlOperation(custom_queries.listPublicJournals, {
          filter: { content: { contains: value } },
          limit: 100,
          nextToken,
        })
      )
      return data.data.listPublicJournals
    } catch (err) {
      console.log('error getting journal: ', err)
    }
  }
}

// Get Public Journal Entry
// -----------------------------------------------------------------------------
export function getPublicJournalEntry(journalUid) {
  return async (dispatch) => {
    dispatch({ type: 'JOURNAL_LOADING_STATUS', loading: true })
    // dispatch({ type: "JOURNAL_AVAILABLE", currentJournal: null })
    try {
      const data = await API.graphql({
        query: custom_queries.getPublicJournal,
        variables: {
          id: journalUid,
        },
        authMode: 'AWS_IAM',
      })
      dispatch({
        type: 'JOURNAL_AVAILABLE',
        currentJournal: data.data.getPublicJournal,
      })
      return data.data.getPublicJournal
    } catch (err) {
      console.log('error getting journal: ', err)
    }
  }
}

// Create User Love Journal
// -----------------------------------------------------------------------------
export function createUserLovedJournal(
  userLovedJournalJournalId,
  userLovedJournalUserId,
  journalAuthorId
) {
  return async (dispatch, getState) => {
    try {
      // One love per user and journal: a second create of this ID is refused. The count is
      // kept by gnjAmplifyCounters from the table stream (security sprint PR 5).
      const data = await API.graphql(
        graphqlOperation(custom_mutations.createUserLovedJournal, {
          input: {
            id: loveId(userLovedJournalUserId, userLovedJournalJournalId),
            userLovedJournalJournalId,
            userLovedJournalUserId,
          },
        })
      )

      createNotificationFromLovedJournal(
        userLovedJournalJournalId,
        journalAuthorId,
        getState().user.userData
      )

      return data.data.createUserLovedJournal
    } catch (err) {
      // Already loved (another tab, or a click before the page knew): that row is this love.
      if (alreadyExists(err)) {
        return { id: loveId(userLovedJournalUserId, userLovedJournalJournalId) }
      }
      if (err.errors[0].errorType === 'Unauthorized') {
        createNotificationFromLovedJournal(
          userLovedJournalJournalId,
          journalAuthorId,
          getState().user.userData
        )
      } else {
        console.log('error Create User Love Journal: ', err)
      }
    }
  }
}

// Delete User Love Journal
// -----------------------------------------------------------------------------
export function deleteUserLovedJournal(id) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.deleteUserLovedJournal, {
          input: { id },
        })
      )

      return data.data.deleteUserLovedJournal
    } catch (err) {
      console.log('error Delete User Love Journal: ', err)
    }
  }
}

// Create User Love Comment
// -----------------------------------------------------------------------------
export function createUserLovedComment(
  comment,
  userLovedCommentUserId,
  journalID
) {
  return async (dispatch, getState) => {
    try {
      // One love per user and comment; the count is kept server-side (security sprint PR 5).
      const data = await API.graphql(
        graphqlOperation(custom_mutations.createUserLovedComment, {
          input: {
            id: loveId(userLovedCommentUserId, comment.id),
            userLovedCommentCommentId: comment.id,
            userLovedCommentUserId,
          },
        })
      )
      createNotificationFromLovedComment(
        comment,
        journalID,
        getState().user.userData
      )
      return data.data.createUserLovedComment
    } catch (err) {
      if (alreadyExists(err)) return { id: loveId(userLovedCommentUserId, comment.id) }
      if (err.errors[0].errorType === 'Unauthorized') {
        createNotificationFromLovedComment(
          comment,
          journalID,
          getState().user.userData
        )
      } else {
        console.log('errer creating user loved comment: ', err)
      }
    }
  }
}

// Delete User Love Comment
// -----------------------------------------------------------------------------
export function deleteUserLovedComment(comment) {
  return async (dispatch) => {
    try {
      await API.graphql(
        graphqlOperation(custom_mutations.deleteUserLovedComment, {
          input: { id: comment.hasLoved },
        })
      )
    } catch (err) {
      console.log('error deleting user loved comment: ', err)
    }
  }
}

// Get Loved users from Public Journal Entry
// -----------------------------------------------------------------------------
export function getLovedUsersFromJournal(journalUid, nextToken) {
  return async (dispatch) => {
    if (!nextToken)
      dispatch({ type: 'SET_LOVERLOADING_STATUS', loverLoading: true })
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.getLoveUserJournal, {
          id: journalUid,
          lovedNextToken: nextToken,
        })
      )
      if (nextToken)
        dispatch({
          type: 'ADD_LOVED_USERS',
          lovedUsers: data.data.getPublicJournal.lovedUsers,
        })
      else
        dispatch({
          type: 'SET_LOVED_USERS',
          lovedUsers: data.data.getPublicJournal.lovedUsers,
        })
      dispatch({ type: 'SET_LOVERLOADING_STATUS', loverLoading: false })
    } catch (err) {
      console.log('error getting loved user from journal: ', err)
      dispatch({ type: 'SET_LOVERLOADING_STATUS', loverLoading: false })
    }
  }
}

// Get Loved users from Comment
// -----------------------------------------------------------------------------
export function getLovedUsersFromComment(commendUid, nextToken) {
  return async (dispatch) => {
    if (!nextToken)
      dispatch({ type: 'SET_LOVERLOADING_STATUS', loverLoading: true })
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.getLoveUserComment, {
          id: commendUid,
          lovedNextToken: nextToken,
        })
      )
      if (nextToken)
        dispatch({
          type: 'ADD_LOVED_USERS',
          lovedUsers: data.data.getComment.lovedUsers,
        })
      else
        dispatch({
          type: 'SET_LOVED_USERS',
          lovedUsers: data.data.getComment.lovedUsers,
        })
      dispatch({ type: 'SET_LOVERLOADING_STATUS', loverLoading: false })
    } catch (err) {
      console.log('error getting loved user from comment ', err)
      dispatch({ type: 'SET_LOVERLOADING_STATUS', loverLoading: false })
    }
  }
}

// Check Challenge table with username and yearMonth then excute functions accordingly
// -----------------------------------------------------------------------------
export function checkIfUserHasJournalAlready(challengeData) {
  return async (dispatch, getState) => {
    try {
      const currentYear = new Date().getFullYear()
      const currentMonth = new Date().getMonth()
      const yearMonth = currentYear + '-' + ('0' + (currentMonth + 1)).slice(-2)
      const data = await API.graphql(
        graphqlOperation(custom_queries.yearMonthUserAsKey, {
          yearMonth: yearMonth,
          user: { eq: challengeData.userUid },
        })
      )
      //Excute functions accordingly
      if (challengeData.request === 'WRITE') {
        if (data.data.yearMonthUserAsKey.items.length === 0) {
          dispatch(
            createChallenge(challengeData, yearMonth, getState().user.userData)
          )
        } else {
          dispatch(
            updateChallenge(
              data.data.yearMonthUserAsKey.items[0].id,
              data.data.yearMonthUserAsKey.items[0].journalsPerMonth + 1,
              getState().user.userData,
              data.data.yearMonthUserAsKey.items[0].journalWordCountPerMonth +
                challengeData.journalWordCountPerMonth
            )
          )
        }
      } else if (challengeData.request === 'UPDATE') {
        const countJournalWordCountPerMonth =
          challengeData.journalWordCountBeforeEditing
            ? data.data.yearMonthUserAsKey.items[0].journalWordCountPerMonth +
              (challengeData.journalWordCountPerMonth -
                challengeData.journalWordCountBeforeEditing)
            : data.data.yearMonthUserAsKey.items[0].journalWordCountPerMonth +
              challengeData.journalWordCountPerMonth

        dispatch(
          updateChallenge(
            data.data.yearMonthUserAsKey.items[0].id,
            data.data.yearMonthUserAsKey.items[0].journalsPerMonth,
            getState().user.userData,
            countJournalWordCountPerMonth
          )
        )
      } else {
        if (data.data.yearMonthUserAsKey.items[0].journalsPerMonth <= 1) {
          dispatch(deleteChallenge(data.data.yearMonthUserAsKey.items[0].id))
        } else {
          dispatch(
            updateChallenge(
              data.data.yearMonthUserAsKey.items[0].id,
              data.data.yearMonthUserAsKey.items[0].journalsPerMonth - 1,
              getState().user.userData,
              data.data.yearMonthUserAsKey.items[0].journalWordCountPerMonth -
                challengeData.journalWordCountPerMonth
            )
          )
        }
      }
    } catch (e) {
      console.log('checkIfUserHasJournalAlready ', e)
    }
  }
}

// Add item to Challenge table
// -----------------------------------------------------------------------------
export function createChallenge(challengeData, yearMonth, userData) {
  return async () => {
    try {
      await API.graphql(
        graphqlOperation(custom_mutations.createChallenge, {
          input: {
            yearMonth: yearMonth,
            user: challengeData.userUid,
            journalsPerMonth: 1,
            journalWordCountPerMonth: challengeData.journalWordCountPerMonth,
            userProfileImage: userData.profileImage
              ? userData.profileImage
              : null,
            userDisplayName: userData.displayName ? userData.displayName : null,
          },
        })
      )
    } catch (err) {
      console.log('createChallenge ', err)
    }
  }
}

// Remove item on Challenge table
// -----------------------------------------------------------------------------
export function deleteChallenge(challengeUid) {
  return async () => {
    try {
      await API.graphql(
        graphqlOperation(custom_mutations.deleteChallenge, {
          input: {
            id: challengeUid,
          },
        })
      )
    } catch (err) {
      console.log('deleteChallenge ', err)
    }
  }
}

// Update Challenge table
// -----------------------------------------------------------------------------
export function updateChallenge(
  challengeUid,
  newNumberOfJournal,
  userData,
  journalWordCountPerMonth
) {
  return async () => {
    try {
      await API.graphql(
        graphqlOperation(custom_mutations.updateChallenge, {
          input: {
            id: challengeUid,
            journalsPerMonth: newNumberOfJournal,
            journalWordCountPerMonth:
              journalWordCountPerMonth < 0 ? 0 : journalWordCountPerMonth,
            userProfileImage: userData.profileImage
              ? userData.profileImage
              : null,
            userDisplayName: userData.displayName ? userData.displayName : null,
          },
        })
      )
    } catch (err) {
      console.log('updateChallenge ', err)
    }
  }
}

// Check is current user loved journal or not
// -----------------------------------------------------------------------------
export function isUserLovedJournal(user, journal) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.isUserLovedJournalAsKey, {
          userLovedJournalUserId: user,
          userLovedJournalJournalId: { eq: journal },
        })
      )
      return data.data.isUserLovedJournalAsKey
    } catch (err) {
      console.log('isUserLovedJournal ', err)
    }
  }
}

// Check is current user loved comment or not
// -----------------------------------------------------------------------------
export function isUserLovedComment(user, comment) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.isUserLovedCommentAsKey, {
          userLovedCommentUserId: user,
          userLovedCommentCommentId: { eq: comment },
        })
      )
      return data.data.isUserLovedCommentAsKey
    } catch (err) {
      // console.log('isUserLovedComment ', err)
    }
  }
}

// Check if user has the book with the same name already
// -----------------------------------------------------------------------------
export function isUserHasABook(user, bookName) {
  return async () => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.isUserHasABookAsKey, {
          bookUserId: user,
          book: { eq: bookName },
        })
      )
      return data.data.isUserHasABookAsKey
    } catch (err) {
      console.log('isUserHasABook ', err)
    }
  }
}

const BOOK_CREATE_FAILED = "Your book couldn't be created. Check your connection and try again."

// Create a book
// -----------------------------------------------------------------------------
export function createABook(bookData) {
  return async (dispatch, getState) => {
    const username = getState().user.userData.id
    const numberOfBooks = getState().user.userData.books.items.length

    // Create up to 30 books
    if (numberOfBooks >= 30) {
      return { error: 'You have reached the maximum number(30) of books.' }
    }

    // check if user has the book with the same name already
    const checkedBookName = await dispatch(
      isUserHasABook(username, bookData.book)
    )
    // undefined: the check itself failed (it logs the error).
    if (!checkedBookName) {
      return { error: BOOK_CREATE_FAILED, membersOnly: false }
    }
    if (checkedBookName.items.length > 0) {
      return { error: 'Book name exists already' }
    } else {
      // Call this with or without cover image
      async function createABookReqeust(coverImage) {
        // gql: a book written with only a related row unreadable still counts as created.
        const data = await gql(
          graphqlOperation(custom_mutations.createBook, {
            input: {
              book: bookData.book,
              bookUserId: username,
              bookCoverImg: coverImage,
            },
          })
        )

        dispatch({ type: 'CREATE_USER_BOOK', userBooks: data.data.createBook })
        // return data.data.createBook
      }

      try {
        if (bookData.bookCoverImg) {
          const coverImage = await dispatch(
            uploadBookCoverImage(username, bookData.bookCoverImg)
          )
          await createABookReqeust(coverImage)
        } else {
          await createABookReqeust()
        }
      } catch (err) {
        console.log('createABook ', err)
        // The pages show `error` in the popup; `membersOnly` opens the upgrade popup instead.
        const refusal = refusalOf(err)
        return {
          error: refusal ? "You can't create this book." : BOOK_CREATE_FAILED,
          membersOnly: refusal === MEMBERS_ONLY,
        }
      }
    }
  }
}

// Upload book cover image to S3
// -----------------------------------------------------------------------------
export function uploadBookCoverImage(username, file) {
  return async () => {
    const extension = /^.+\.([^.]+)$/.exec(file.name)[1]
    const { type: mimeType } = file
    const dataToURL = new Date()
      .toJSON()
      .slice(0, 23)
      .replace(/[^0-9-]/g, '-')
    const filePath = `book/${username}-book-${dataToURL}.${extension}`

    try {
      // Uploading to S3 bucket
      const { path } = await Storage.put(ownPublicPath(filePath), file, {
        contentType: mimeType,
      })
      return buildS3Url(path)
    } catch (err) {
      console.log('error uploadBookCoverImage: ', err)
    }
  }
}

// Get More User Books
// -----------------------------------------------------------------------------
export function getMoreUserBooks(username, nextToken) {
  return async (dispatch) => {
    try {
      const data = await graphqlGetUser(
        graphqlOperation(custom_queries.getUser, {
          id: username,
          nextTokenBook: nextToken,
        })
      )
      const newBookData = {
        items: data.data.getUser.books.items,
        nextToken: data.data.getUser.books.nextToken,
      }
      dispatch({ type: 'ADD_USER_BOOK_DATA', newBookData })
    } catch (err) {
      console.log('error getMoreUserBooks: ', err)
    }
  }
}

// Get a Book with journals
// -----------------------------------------------------------------------------
export function getABookWithJournals(
  bookID,
  nextTokenJournal,
  nextTokenPublicJournal
) {
  return async (dispatch) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_queries.getBook, {
          id: bookID,
          nextTokenJournal: nextTokenJournal,
          nextTokenPublicJournal: nextTokenPublicJournal,
        })
      )
      return data.data.getBook
    } catch (err) {
      console.log('error getABookWithJournals: ', err)
    }
  }
}

// Delete a book
// -----------------------------------------------------------------------------
export function deleteABook(bookID) {
  return async (dispatch) => {
    try {
      const data = await API.graphql(
        graphqlOperation(custom_mutations.deleteBook, { input: { id: bookID } })
      )
      dispatch({ type: 'REMOVE_A_BOOK', deletedBook: data.data.deleteBook })
      // return data.data.deleteBook
    } catch (err) {
      console.log('error deleteABook: ', err)
    }
  }
}

// Update a book
// -----------------------------------------------------------------------------
export function updateABook(bookData) {
  return async (dispatch, getState) => {
    const username = getState().user.userData.id

    try {
      let coverImage = bookData.bookCoverImg
      if (bookData.newImage) {
        coverImage = await dispatch(
          uploadBookCoverImage(username, bookData.bookCoverImg)
        )
      }

      const data = await gql(
        graphqlOperation(custom_mutations.updateBook, {
          input: {
            id: bookData.id,
            book: bookData.book,
            bookCoverImg: coverImage,
            bookJournalCount: bookData.bookJournalCount,
          },
        })
      )

      dispatch({ type: 'UPDATE_A_BOOK', updatedBook: [data.data.updateBook] })
      return data.data.updateBook
    } catch (err) {
      console.log('error updateABook: ', err)
      // The page keeps its popup open with this message (containers/BookJournals.js).
      return {
        error: refusalOf(err)
          ? "You can't change this book."
          : "Your book couldn't be saved. Check your connection and try again.",
      }
    }
  }
}

// Update book journal number count
// -----------------------------------------------------------------------------
export function adjustBookJournalsCount(bookId, diff) {
  return async (dispatch, getState) => {
    let count = 0
    let maxTries = 3

    while (true) {
      try {
        const getTotalJournalsOfTheBook = getState().user.userBooks.filter(
          (book) => {
            return book.id === bookId
          }
        )
        let total =
          getTotalJournalsOfTheBook[0] &&
          getTotalJournalsOfTheBook[0].bookJournalCount
            ? getTotalJournalsOfTheBook[0].bookJournalCount + diff
            : diff
        if (total < 0) total = 0
        const data = await API.graphql(
          graphqlOperation(custom_mutations.updateBook, {
            input: {
              id: bookId,
              bookJournalCount: total,
            },
          })
        )

        dispatch({ type: 'UPDATE_A_BOOK', updatedBook: [data.data.updateBook] })
        break
      } catch (err) {
        console.log('error adjustBookJournalsCount: ', err)
        if (++count === maxTries) throw err
      }
    }
  }
}
